import { windowIndexes, type ChartModel, type DeviceLine } from './chartModel';
import type { Device } from './devices';
import type { ChartMeasure } from './measures';

/** One tooltip row: a chart and each device's value at the hovered instant, `null` where its line isn't drawn. */
export interface HoverRow {
  measure: ChartMeasure;
  values: (number | null)[];
}

const VALUE_DECIMALS = 2;

const nearestIn = (
  t: readonly number[],
  at: number,
  start: number,
  end: number
): number => {
  const [firstAfter] = windowIndexes(t, at, Infinity);
  const hi = Math.min(Math.max(firstAfter, start), end);
  const lo = Math.max(hi - 1, start);
  return at - t[lo] <= t[hi] - at ? lo : hi;
};

/** The time of the reading closest to `at` across every line, or `null` when there are no readings. */
export const snapTime = (
  models: readonly ChartModel[],
  at: number
): number | null => {
  let best: number | null = null;
  for (const { lines } of models) {
    for (const { t } of lines) {
      if (t.length === 0) continue;
      const time = t[nearestIn(t, at, 0, t.length - 1)];
      if (best === null || Math.abs(time - at) < Math.abs(best - at)) {
        best = time;
      }
    }
  }
  return best;
};

/**
 * A line's reading closest to `at`, or `null` when `at` falls in a gap or outside its readings and no reading is
 * within `tolerance` ms, so a lone dot or a segment's end still reads when the snap lands just beside it.
 */
export const valueAt = (
  line: DeviceLine,
  at: number,
  tolerance = 0
): number | null => {
  const { t, v, segments } = line;
  if (t.length === 0) return null;
  const segment = segments.find(
    ([start, end]) => t[start] <= at && at <= t[end]
  );
  if (segment) return v[nearestIn(t, at, segment[0], segment[1])];
  const nearest = nearestIn(t, at, 0, t.length - 1);
  return Math.abs(t[nearest] - at) <= tolerance ? v[nearest] : null;
};

/** Each chart's values at `at`, one per device in `devices` order; see {@link valueAt} for `tolerance`. */
export const hoverRows = (
  models: readonly ChartModel[],
  devices: readonly Device[],
  at: number,
  tolerance = 0
): HoverRow[] =>
  models.map(({ measure, lines }) => ({
    measure,
    values: devices.map((device) => {
      const line = lines.find((candidate) => candidate.device.id === device.id);
      return line ? valueAt(line, at, tolerance) : null;
    }),
  }));

/** A reading with its unit, to at most two decimals, e.g. "3.25°C", "80%". */
export const formatReading = (value: number, unit: string): string =>
  `${Number(value.toFixed(VALUE_DECIMALS))}${unit}`;
